/**
 * The real adapter onto `@wtfalch/files`, now that files#71 (audience) and
 * files#72 (mine/shared with me/search) have both shipped -- see
 * `source.ts`'s own doc. Built with the SDK's browser same-origin-proxy
 * mode (`@wtfalch/files`' README, "Calling from a browser through a
 * same-origin proxy"): `App.tsx` points this at the agora's own origin, and
 * every request path gets `/_files` spliced back on before it leaves the
 * browser, matching the Caddy route that already forwards it to the files
 * host.
 *
 * Three gaps the real files service cannot close yet, each left honest
 * rather than papered over with a fabricated value:
 *
 * - `updatedAt` is always `null`. `StoredFile` carries no per-file
 *   timestamp the client can read -- the database row has one
 *   (`files_files.created_at`), it is simply never put on the wire. Filed
 *   as wtfalch/files#111.
 * - `people()`/`teams()` both return `[]`. There is no directory endpoint a
 *   signed-in person's own token can call: `files.members:read` is a
 *   signed-request-only management route, not one of the `/v1/*` routes
 *   person tokens are accepted on (`apps/host/README.md`, "Person tokens").
 *   Until Agora has some org-wide directory of its own, the upload dialog's
 *   "People"/"A team" pickers simply have nothing to offer; "Only me" and
 *   "Everyone in the company" still work.
 * - A shared file's owner has no name, only `SharedFile.owner.id`/`.class` --
 *   the id stands in for the name for the same reason: nothing here can
 *   resolve it to one.
 */

import type { SharedFile, StoredFile } from '@wtfalch/files';
import { createFilesClient } from '@wtfalch/files';

import type { AgoraFile, Audience, FilesSource, Person } from './source.ts';

function toFileAudience(audience: Audience): string[] {
  switch (audience.kind) {
    case 'me':
      return [];
    case 'people':
      return audience.subjects.map((subject) => `person:${subject}`);
    case 'team':
      return [`team:${audience.teamId}`];
    case 'company':
      return ['org'];
  }
}

/** The reverse of `toFileAudience`, for a file this browser did not just
 *  upload itself. Checked in the same explicit-first order the SDK's own
 *  README lists the references in: `org` (the plainest, most explicit one)
 *  before a `team:` reference before any `person:` references. A `group:`
 *  reference (issue #71's fourth kind, e.g. a chat room's shared files) has
 *  no `Audience` kind of its own here -- folding it into `company` errs
 *  toward showing a file as shared rather than silently hiding that it is. */
function fromFileAudience(refs: readonly string[]): Audience {
  if (refs.length === 0) return { kind: 'me' };
  if (refs.includes('org')) return { kind: 'company' };
  const teamRef = refs.find((ref) => ref.startsWith('team:'));
  if (teamRef) return { kind: 'team', teamId: teamRef.slice('team:'.length) };
  const subjects = refs
    .filter((ref) => ref.startsWith('person:'))
    .map((ref) => ref.slice('person:'.length));
  if (subjects.length > 0) return { kind: 'people', subjects };
  return { kind: 'company' };
}

function fromStoredFile(file: StoredFile, owner: Person): AgoraFile {
  return {
    id: file.id,
    name: file.name,
    contentType: file.contentType,
    bytes: Number(file.bytes),
    updatedAt: null,
    owner,
    audience: fromFileAudience(file.audience),
  };
}

function fromSharedFile(file: SharedFile): AgoraFile {
  return fromStoredFile(file, { subject: file.owner.id, name: file.owner.id });
}

export function realFiles(options: {
  /** Agora's own origin, e.g. `https://agora.example.com` -- never the
   *  files host's own address, which a browser cannot reach at all (no
   *  CORS, no public domain). */
  origin: string;
  organisationId: string;
  getAccessToken: () => string;
  /** The signed-in person themselves -- `StoredFile` carries no owner of
   *  its own (unlike `SharedFile`), so `mine()` has to supply it. A
   *  function, like `getAccessToken`, so a name that only resolves after
   *  this source was created (Mail's own identity fetch) still reaches
   *  files uploaded or listed after it does. */
  me: () => Person;
  /** The one collection every upload goes into (`agoras.ts`'s own doc on
   *  `Agora.filesCollectionId`) -- `undefined` when an operator has not
   *  provisioned one yet, in which case browsing and downloading still
   *  work and only `upload()` refuses. */
  collectionId: string | undefined;
}): FilesSource {
  const client = createFilesClient({
    url: options.origin,
    credential: () => options.getAccessToken(),
    organisationId: options.organisationId,
    // Only needed because the proxy is mounted under `/_files` rather than
    // at the origin's own root -- the SDK always builds request paths from
    // `/v1/...`, so that prefix has to go back on before the real request
    // leaves the browser (`@wtfalch/files`' README, same section).
    fetch: (input, init) => {
      const target = new URL(input instanceof Request ? input.url : input);
      target.pathname = `/_files${target.pathname}`;
      return fetch(target, init);
    },
  });

  return {
    mine: async () => {
      const page = await client.listMyFiles();
      return page.files.map((file) => fromStoredFile(file, options.me()));
    },
    shared: async () => {
      const page = await client.listSharedFiles();
      return page.files.map(fromSharedFile);
    },
    search: async (query) => {
      const [mine, shared] = await Promise.all([
        client.listMyFiles({ name: query }),
        client.listSharedFiles({ name: query }),
      ]);
      return [
        ...mine.files.map((file) => fromStoredFile(file, options.me())),
        ...shared.files.map(fromSharedFile),
      ];
    },
    upload: async (file, audience) => {
      if (!options.collectionId) {
        throw new Error('Files upload is not set up for this company yet.');
      }
      const reservation = await client.reserveUpload({
        collectionId: options.collectionId,
        name: file.name,
        contentType: file.type || 'application/octet-stream',
        bytes: file.size,
        requestId: crypto.randomUUID(),
        retention: 'durable',
        audience: toFileAudience(audience),
      });
      if (reservation.state === 'multipart') {
        throw new Error('This file is too large to upload here.');
      }
      const stored =
        reservation.state === 'confirmed'
          ? await client.getFile(reservation.fileId)
          : await (async () => {
              const response = await fetch(reservation.url, {
                method: 'PUT',
                headers: reservation.headers,
                body: file,
              });
              if (!response.ok) throw new Error('The upload failed.');
              return client.confirmUpload(reservation.uploadId);
            })();
      return fromStoredFile(stored, options.me());
    },
    downloadUrl: async (id) => (await client.downloadFile(id)).url,
    // See this file's own doc: no directory endpoint a person token can
    // call exists yet.
    people: async () => [],
    teams: async () => [],
  };
}
